import { Fragment } from "react";
import { Box, CircularProgress, Dialog, Divider, Link, Chip } from "@mui/material";
import Image from "next/image";
import IconButton from "@mui/material/IconButton";
import IconCheck from "@mui/icons-material/Check";
import { useTranslation, withTranslation, Trans } from "next-i18next";
import CloseIcon from "@icons/Close";
import { IMG_BG_COOKIES_CONSENT, logo } from "@config/images";
import useResponsive from "@helpers/useResponsive";
import Button from "@ds/components/Button";
import Text from "@ds/components/Text";
import color from "@definitions/mui/color";
import { fontFamSecondary } from "@definitions/mui/custom";
import { IMG_COOKIES_CONSENT } from "@config/images";
import useAction from "../hooks/useAction";

interface IConsentDetailProps {
  isOpen: boolean;
  onClose: () => void;
  onClickAcceptAll: () => void;
  onClickAcceptNecessary: () => void;
}

const ConsentDetail: React.FC<IConsentDetailProps> = ({
  isOpen,
  onClose,
  onClickAcceptAll,
  onClickAcceptNecessary
}) => {
  const isMobile = useResponsive("md");
  const { dataConsent, isLoadingDetail } = useAction();
  const { t } = useTranslation('cookies');

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" padding="16px 24px">
        <Image
          src={logo}
          alt="MyTEnS logo"
          width={isMobile ? 66 : 88}
          height={isMobile ? 36 : 48}
          objectFit="contain"
        />
        <IconButton onClick={onClose} size="large">
          <CloseIcon style={{ height: 16, width: 16 }} />
        </IconButton>
      </Box>
      {isLoadingDetail ? <CircularProgress sx={{ margin: "24px auto" }} /> : (
        <Box padding={isMobile ? '16px' : '24px'}>
          <Box
            borderRadius="12px"
            display="flex"
            alignItems="center"
            gap="24px"
            padding="16px 24px"
            sx={{
              background: `url(${IMG_BG_COOKIES_CONSENT}) no-repeat bottom right`,
              backgroundColor: color.primary.lighter,
              ...isMobile && {
                flexDirection: 'column',
                padding: '12px 16px',
                gap: '16px',
                alignItems: 'flex-start',
              }
            }}
          >
            <Image
              src={IMG_COOKIES_CONSENT}
              alt="Cookies Consent"
              width={isMobile ? 80 : 120}
              height={isMobile ? 80 : 120}
            />
            <Box sx={{ flex: 1 }}>
              <Text variant={isMobile ? 'subtitle1' : 'h2'} weight="700">
                {dataConsent?.title || t('popupBox.title')}
              </Text>
              <Text paragraph variant="caption1" sx={{ marginTop: '16px', marginBottom: '8px' }}>
                {dataConsent?.description || t('popupBox.description')}
              </Text>
              <Text paragraph variant="caption1" sx={{ marginBottom: 0 }}>
                <Trans
                  i18nKey="popupBox.privacyPolicy"
                  ns="cookies"
                  components={{
                    redirect: (
                      <Link
                        href="/kebijakan-privasi"
                        fontSize="12px"
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    )
                  }}
                />
              </Text>
            </Box>
          </Box>
          {dataConsent?.cookies?.map((cookie, index) => (
            <Fragment key={index}>
              <Box paddingY={isMobile ? '16px' : '24px'}>
                <Box display="flex" alignItems="center" gap="12px">
                  <Text variant={isMobile ? 'subtitle2' : 'h3'} weight="700">{cookie.title}</Text>
                  <Chip
                    label={cookie.status}
                    color="primary"
                    variant="outlined"
                  />
                </Box>
                <Box
                  dangerouslySetInnerHTML={{ __html: String(cookie.description) }}
                  color={color.general.main}
                  fontFamily={fontFamSecondary}
                  fontSize="12px"
                  marginTop="12px"
                />
                {cookie.details.map((detail, dIndex) => (
                  <Box key={dIndex} marginTop={isMobile ? '16px' : '24px'}>
                    <Text variant="subtitle2" weight="700">{detail.title}</Text>
                    <Text paragraph variant="caption1" color="inactive-secondary" sx={{
                      marginTop: isMobile ? '8px' : '12px',
                      marginBottom: '8px',
                    }}>
                      {detail.description}
                    </Text>
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      gap="8px 12px"
                      marginTop="8px"
                      border="1px solid #DEE3ED"
                      padding="8px 12px"
                      borderRadius="4px"
                      width="fit-content"
                      sx={{
                        ...isMobile && {
                          gap: '4px 8px',
                          padding: '4px 8px',
                        }
                      }}
                    >
                      {detail.components.map((item, iIndex) => (
                        <Fragment key={iIndex}>
                          {item.url ? (
                            <Link
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              color={color.primary.main}
                              underline="none"
                              fontSize="12px"
                              fontWeight="600"
                              fontFamily={fontFamSecondary}
                            >
                              {item.name}
                            </Link>
                          ) : (
                            <Text paragraph variant="caption1" weight="600" sx={{ marginBottom: 0 }}>
                              {item.name}
                            </Text>
                          )}
                          {iIndex < detail.components.length - 1 &&
                            <Divider orientation="vertical" variant="middle" flexItem sx={{ margin: 0 }} />}
                        </Fragment>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
              {index < dataConsent.cookies.length - 1 && <Divider />}
            </Fragment>
          ))}
        </Box>
      )}
      <Box display="flex" gap="16px" padding="16px 24px" borderTop="1px solid #DEE3ED" sx={{
        ...isMobile && {
          flexDirection: 'column-reverse',
          gap: '8px',
          padding: '12px 16px',
        }
      }}>
        <Button
          variant="outlined"
          size={isMobile ? 'small' : 'medium'}
          fullWidth
          onClick={onClickAcceptNecessary}
        >
          {t('button.acceptNecessary')}
        </Button>
        <Button
          size={isMobile ? 'small' : 'medium'}
          fullWidth
          onClick={onClickAcceptAll}
        >
          {t('button.acceptAll')}
          <IconCheck sx={{ marginLeft: '8px' }} />
        </Button>
      </Box>
    </Dialog>
  );
};

export default withTranslation(['cookies'])(ConsentDetail);
